// BigFinanceOnlyNotice — the one line a BigFinance-only login sees on this
// app's home (Jason, 5 Oct 2026 late: the accountant's "first sign-in name[s]
// where BigFinance lives, with the link").
//
// An outside accountant invited into BigFinance only holds NO membership, yet
// the invite's set-password page (/accept-invite, here) ends on this app's
// home: no business, and every tile a dead end. BigFinance is its own app at
// its own address, so this names it, from the address the invite wrote into
// their metadata (src/lib/bigFinanceOnly.ts), never a guessed host.
//
// Shown ONLY to a login with no active business whose invite was a BigFinance
// one. Anyone with a membership never sees it: BigFinance is otherwise not
// linked from this app (it is kept apart on purpose), and this reaches only
// the person who has nothing else here.
import { useMemo } from 'react'
import { Landmark } from 'lucide-react'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { decodeStoredClaims } from '@/lib/offlineSession'
import { bigFinanceOnlyLogin } from '@/lib/bigFinanceOnly'

export default function BigFinanceOnlyNotice() {
  const { data: tenant } = useActiveTenant()
  // The stored token's claims: no auth lock, and user_metadata rides in it.
  const login = useMemo(() => bigFinanceOnlyLogin(decodeStoredClaims()?.raw?.user_metadata), [])
  // Only once the business read has ANSWERED "none" (data, never isPending/
  // isSuccess — readState's rule). A pending or failed read says nothing: an
  // invitee who has since become a member keeps invited_app in their metadata
  // for good, and must not be told this login is for BigFinance.
  if (tenant === undefined || tenant.tenantId || !login) return null

  return (
    <p role="status" className="mb-4 card p-3 flex items-start gap-2 text-sm text-fg-secondary">
      <Landmark size={16} className="text-emerald-400 shrink-0 mt-0.5" aria-hidden />
      {login.url ? (
        <span>
          This login is for BigAccounting, which is its own app:{' '}
          <a href={login.url} className="text-blue-400 hover:text-blue-300 underline break-all">{new URL(login.url).host}</a>.
          {' '}Sign in there with the same email address and password.
        </span>
      ) : (
        <span>
          This login is for BigAccounting, which is its own app with its own address. Ask whoever invited you for it,
          and sign in there with the same email address and password.
        </span>
      )}
    </p>
  )
}
